import type { AbstractPowerSyncDatabase } from "@powersync/common";
import { USER_DATA_TABLES } from "@stockpilot/powersync";

/**
 * Tablas de inventario org-scoped (Stockpilot F1): el dato del visitante usa
 * `organization_id = VISITOR_ID`; al iniciar sesión migra a la org personal del
 * usuario (organization_id = newUserId), igual que el sweep de `user_id`.
 */
const INVENTORY_ORG_TABLES = [
  "product",
  "product_category",
  "uom",
  "warehouse",
  "location",
  "stock_move",
  "stock_quant",
  // F2: operaciones + reabastecimiento.
  "stock_picking",
  "stock_move_line",
  "reorder_rule",
  // F3: compras + valoración.
  "vendor",
  "product_supplier",
  "purchase_order",
  "purchase_order_line",
  "valuation_layer",
  // F4: trazabilidad (lotes/series) + conteos.
  "lot",
  "inventory_count",
  "inventory_count_line",
  // F5: manufactura + landed costs + devoluciones a proveedor.
  "bill_of_materials",
  "bom_component",
  "manufacturing_order",
  "landed_cost",
  "landed_cost_line",
  "purchase_return",
  "purchase_return_line",
] as const;

/**
 * Replaces VISITOR_ID with the newly authenticated user's ID
 * across all relevant tables and the offline CRUD queue.
 *
 * This ensures that:
 * 1. The local UI instantly reflects the new owner without flashing.
 * 2. The PowerSync upload queue sends the correct `user_id` / `organization_id`
 *    to the backend.
 */
export async function sweepVisitorData(
  db: AbstractPowerSyncDatabase | null,
  newUserId: string,
  visitorId: string,
) {
  if (!db || !newUserId) {
    return;
  }

  try {
    // 1. Update the actual table rows so UI queries pick them up immediately
    await Promise.all(
      USER_DATA_TABLES.map(async (table) =>
        db.execute(`UPDATE ${table} SET user_id = ? WHERE user_id = ?`, [newUserId, visitorId]),
      ),
    );

    // 1b. Inventory rows are org-scoped: migrate organization_id VISITOR → user.
    await Promise.all(
      INVENTORY_ORG_TABLES.map(async (table) =>
        db.execute(`UPDATE ${table} SET organization_id = ? WHERE organization_id = ?`, [
          newUserId,
          visitorId,
        ]),
      ),
    );

    // 2. Rewrite the queued operations so the network payload has the correct id.
    //
    // PowerSync's `ps_crud.data` column is a JSON envelope
    // `{"op":"PUT","type":"<table>","id":"<uuid>","data":{<column>:<value>,…}}`
    // — the row's ACTUAL column values live NESTED under `$.data.*`, not at the
    // top level (see `CrudEntry.fromRow`: `opData = parsed.data`). So the visitor
    // id must be rewritten at `$.data.user_id` / `$.data.organization_id`; the
    // previous top-level `$.user_id` / `$.organization_id` paths matched NOTHING,
    // leaving the queued op stamped with `VISITOR_ID`.
    //
    // For user_id-scoped clones this was harmless (the server derives ownership
    // from the JWT `sub`, ignoring the payload id). But an ORG-scoped server
    // (Stockpilot) authorizes each write by the payload `organization_id` against
    // `member` — a queued PUT still carrying `organization_id = VISITOR_ID` fails
    // the membership check and is SILENTLY dropped (returns 200, inserts nothing),
    // so the visitor's inventory never round-trips after "Keep and merge".
    await db.execute(
      `
      UPDATE ps_crud
      SET data = json_set(data, '$.data.user_id', ?)
      WHERE json_extract(data, '$.data.user_id') = ?
    `,
      [newUserId, visitorId],
    );
    await db.execute(
      `
      UPDATE ps_crud
      SET data = json_set(data, '$.data.organization_id', ?)
      WHERE json_extract(data, '$.data.organization_id') = ?
    `,
      [newUserId, visitorId],
    );
  } catch (error) {
    console.error("[Sweep] Failed to sweep visitor data:", error);
  }
}
